/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { isAdminUser, getUserVenues, userCanAccessVenue } from '../userUtils';
import { UtensilsCrossed, Coffee, ShoppingCart, X, AlertTriangle, CheckCircle, ClipboardList, ChefHat } from 'lucide-react';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

// ── Amend deadline: 2am on the delivery date ─────────────────────────
// Returns { isPast, label } — updates every 30s.
// deliveryDate is a YYYY-MM-DD string (stored on the request row).
function useAmendDeadline(deliveryDate) {
  const getState = useCallback(() => {
    if (!deliveryDate) return { isPast: false, label: '' };
    // 2am local time on delivery date
    const deadline = new Date(deliveryDate + 'T02:00:00');
    const now = new Date();
    const diffMs = deadline - now;
    if (diffMs <= 0) return { isPast: true, label: '' };
    const totalMins = Math.floor(diffMs / 60000);
    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    const deadlineStr = deadline.toLocaleDateString('en-GB', { weekday: 'short' })
      + ' 2:00am';
    if (hrs >= 2) return { isPast: false, label: `Can be changed until ${deadlineStr}` };
    if (hrs >= 1) return { isPast: false, label: `${hrs}h ${mins}m left to amend` };
    return { isPast: false, label: `${mins} minute${mins !== 1 ? 's' : ''} left to amend — hurry!` };
  }, [deliveryDate]);

  const [state, setState] = useState(getState);
  useEffect(() => {
    setState(getState());
    const t = setInterval(() => setState(getState()), 30000);
    return () => clearInterval(t);
  }, [getState]);
  return state;
}

const ALL_VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys'];
const INTERNAL_VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen'];

const VENUE_IDENTITY = {
  'Box Jordaan':  { letter: 'J',  bg: '#BAD4F5', text: '#2563EB', border: '#93C5FD' },
  'Box Plantage': { letter: 'P',  bg: '#B8DDD3', text: '#2D7A65', border: '#7EA594' },
  'Sassys':       { letter: 'S',  bg: '#D4B8E0', text: '#6D28A0', border: '#C4B5FD' },
  'Prep Kitchen': { letter: 'PK', bg: '#F2B8CB', text: '#C4607A', border: '#F2B8CB' },
};

const C = {
  yellow: '#F7EC71', yellowLight: '#FEFCE8', yellowDark: '#E4AC49',
  orange: '#E4AC49', orangeLight: '#FDF3DC',
  green: '#7EA594', greenLight: '#EAF2EF',
  cream: '#F7F6EF', charcoal: '#2F3A42',
  navy: '#445366', mid: '#7A8A96', border: '#D8DEE3',
  skyBlue: '#BCD9E5', skyBlueLight: '#EAF4F8', skyText: '#3A8FA3',
  white: '#FFFFFF', red: '#DC3545', redLight: '#FFF0F0',
  pink: '#F2B8CB', pinkLight: '#FDF0F5', pinkText: '#C4607A',
  amber: '#E07B00', amberLight: '#FFF3E0',
};

const pill = (a) => ({ padding: '0.35rem 1rem', borderRadius: '999px', border: `1.5px solid ${a ? C.navy : C.border}`, background: a ? C.navy : C.white, color: a ? C.white : C.mid, fontSize: '0.83rem', cursor: 'pointer', fontWeight: a ? 700 : 500, transition: 'all 0.15s ease', whiteSpace: 'nowrap' });
const categoryPill = (a) => ({ padding: '0.28rem 0.85rem', borderRadius: '999px', border: `1.5px solid ${a ? C.navy : C.border}`, background: a ? C.navy : C.white, color: a ? C.white : C.mid, fontSize: '0.78rem', cursor: 'pointer', fontWeight: a ? 700 : 500, transition: 'all 0.15s ease', whiteSpace: 'nowrap' });
const SL = { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.navy, marginBottom: '0.6rem' };

// ── Cart draft persistence (localStorage, 6h expiry) ─────────────
const DRAFT_TTL_MS = 6 * 60 * 60 * 1000;
function draftKey(venue, orderFrom) { return `cart_draft_${venue}_${orderFrom}`; }
function saveCartDraft(venue, orderFrom, { cart, orderNotes, parCountsDone }) {
  if (!venue || !orderFrom) return;
  try {
    localStorage.setItem(draftKey(venue, orderFrom), JSON.stringify({ cart, orderNotes, parCountsDone, savedAt: Date.now() }));
  } catch (e) {}
}
function loadCartDraft(venue, orderFrom) {
  if (!venue || !orderFrom) return null;
  try {
    const raw = localStorage.getItem(draftKey(venue, orderFrom));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) { localStorage.removeItem(draftKey(venue, orderFrom)); return null; }
    return parsed;
  } catch (e) { return null; }
}
function clearCartDraft(venue, orderFrom) {
  if (!venue || !orderFrom) return;
  try { localStorage.removeItem(draftKey(venue, orderFrom)); } catch (e) {}
}

function fmtDate(ds) {
  if (!ds) return '—';
  const d = new Date(ds + 'T12:00:00');
  const t = new Date(); const y = new Date(); y.setDate(t.getDate() - 1);
  if (ds === t.toISOString().split('T')[0]) return 'Today';
  if (ds === y.toISOString().split('T')[0]) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}
function getOpDate() { const now = new Date(); if (now.getHours() < 2) now.setDate(now.getDate() - 1); return now.toISOString().split('T')[0]; }
function getDeliveryDate(opDate) { const d = new Date(opDate + 'T12:00:00'); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; }

// Icon-based dept badge — no emojis
function DeptIcon({ dept, size = 12 }) {
  const isBar = dept === 'bar';
  return isBar
    ? <Coffee size={size} strokeWidth={2} style={{ color: C.pinkText, flexShrink: 0 }} />
    : <ChefHat size={size} strokeWidth={2} style={{ color: C.orange, flexShrink: 0 }} />;
}

function DeptBadge({ dept }) {
  if (!dept) return null;
  const b = dept === 'bar';
  return (
    <span style={{ background: b ? C.pinkLight : C.orangeLight, color: b ? C.pinkText : C.orange, borderRadius: '4px', padding: '0.1rem 0.4rem', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
      <DeptIcon dept={dept} size={10} />
      {b ? 'BAR' : 'KIT'}
    </span>
  );
}

function StatusPill({ status }) {
  const m = { pending: { bg: '#FDF3DC', text: '#E4AC49' }, 'en-route': { bg: '#EAF4F8', text: '#3A8FA3' }, 'partially fulfilled': { bg: '#FEF3C7', text: '#B45309' }, received: { bg: '#EAF2EF', text: '#7EA594' }, confirmed: { bg: '#EAF2EF', text: '#7EA594' } };
  const s = m[status] || { bg: '#F7F6EF', text: '#7A8A96' };
  return <span style={{ background: s.bg, color: s.text, padding: '0.18rem 0.6rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{status}</span>;
}

function LemonBurst() {
  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9999 }}>
      {['🍋', '🌿', '🍋', '🌳', '🍋'].map((e, i) => (<span key={i} style={{ position: 'absolute', fontSize: `${1.2 + Math.random() * 1.2}rem`, top: `${20 + Math.random() * 60}%`, left: `${10 + i * 18 + Math.random() * 10}%`, animation: 'lemonFloat 1.2s ease-out forwards', animationDelay: `${i * 0.08}s`, opacity: 0 }}>{e}</span>))}
      <style>{`@keyframes lemonFloat{0%{opacity:0;transform:translateY(20px) scale(.5)}30%{opacity:1;transform:translateY(-10px) scale(1.1)}80%{opacity:1;transform:translateY(-30px) scale(1)}100%{opacity:0;transform:translateY(-60px) scale(.8)}}`}</style>
    </div>
  );
}

function InitialsModal({ onConfirm, onCancel }) {
  const [initials, setInitials] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.focus(); }, []);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.55)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: C.white, borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '380px', boxShadow: '0 16px 48px rgba(0,0,0,0.22)' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
          <CheckCircle size={32} style={{ color: C.green, marginBottom: '0.5rem' }} />
          <h3 style={{ margin: '0 0 0.35rem', fontSize: '1.1rem', color: C.navy, fontWeight: 800 }}>Confirm Order</h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: C.mid }}>Enter your initials to submit.</p>
        </div>
        <input ref={ref} value={initials} onChange={e => setInitials(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && initials.trim() && onConfirm(initials.trim())}
          placeholder='e.g. EG' maxLength={4}
          style={{ width: '100%', padding: '0.75rem', textAlign: 'center', border: `2px solid ${initials.trim() ? C.navy : C.border}`, borderRadius: '10px', fontSize: '1.4rem', fontWeight: 800, color: C.navy, outline: 'none', letterSpacing: '0.15em', marginBottom: '1.25rem', boxSizing: 'border-box' }} />
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '0.7rem', border: `1.5px solid ${C.border}`, borderRadius: '8px', background: C.white, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', color: C.mid }}>Cancel</button>
          <button onClick={() => initials.trim() && onConfirm(initials.trim())} disabled={!initials.trim()}
            style={{ flex: 2, padding: '0.7rem', background: initials.trim() ? C.yellow : C.cream, color: initials.trim() ? C.charcoal : C.mid, border: 'none', borderRadius: '8px', cursor: initials.trim() ? 'pointer' : 'default', fontWeight: 800, fontSize: '0.95rem' }}>
            Submit Order →
          </button>
        </div>
      </div>
    </div>
  );
}

function SubmitWarningModal({ overages, duplicates, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.5)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: C.white, borderRadius: '14px', padding: '2rem', width: '100%', maxWidth: '480px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <AlertTriangle size={18} style={{ color: C.amber }} />
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: C.navy, fontWeight: 800 }}>Review before submitting</h3>
        </div>

        {duplicates.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: C.skyText, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Already in today's order</div>
            <div style={{ background: C.skyBlueLight, border: `1px solid ${C.skyBlue}`, borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '0.5rem' }}>
              {duplicates.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: i < duplicates.length - 1 ? `1px solid ${C.border}` : 'none', fontSize: '0.88rem' }}>
                  <span style={{ fontWeight: 600, color: C.charcoal }}>{item.name}</span>
                  <span style={{ fontSize: '0.8rem', color: C.mid }}>qty will update to <strong style={{ color: C.navy }}>{item.quantity}</strong></span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '0.75rem', color: C.mid }}>These items are already in today's order. Submitting will overwrite their quantities.</div>
          </div>
        )}

        {overages.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Items over par</div>
            <div style={{ background: C.amberLight, border: `1px solid ${C.amber}40`, borderRadius: '8px', padding: '0.75rem 1rem' }}>
              {overages.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: i < overages.length - 1 ? `1px solid ${C.border}` : 'none', fontSize: '0.88rem' }}>
                  <span style={{ fontWeight: 600, color: C.charcoal }}>{item.name}</span>
                  <span style={{ color: C.mid }}>par <strong style={{ color: C.green }}>{item.par}</strong> · ordering <strong style={{ color: C.amber }}>{item.quantity}</strong></span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '0.55rem 1.25rem', border: `1.5px solid ${C.border}`, borderRadius: '8px', background: C.white, cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', color: C.mid }}>Go back</button>
          <button onClick={onConfirm} style={{ padding: '0.55rem 1.5rem', background: C.yellow, color: C.charcoal, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 800, fontSize: '0.88rem' }}>Submit anyway →</button>
        </div>
      </div>
    </div>
  );
}

// ── Cart modal ────────────────────────────────────────────────────
function CartModal({ cart, orderFrom, venue, onClose, onRemove, onChangeQty, onSubmit, submitting, parMap, notes, onNotesChange, existingOrder, allParDone, parRemaining }) {
  const hasItems = cart.length > 0;
  const isEnRoute = existingOrder?.status === 'en-route' || existingOrder?.status === 'partially fulfilled';
  const isSubmitted = !!(existingOrder && existingOrder.status === 'pending');
  const kitItems = cart.filter(i => (i.dept || '') !== 'bar');
  const barItems = cart.filter(i => (i.dept || '') === 'bar');
  const totalUnits = cart.reduce((s, i) => s + i.quantity, 0);

  // Deadline hook — 2am on delivery date
  const { isPast: deadlinePast, label: deadlineLabel } = useAmendDeadline(existingOrder?.delivery_date);
  const locked = isEnRoute || deadlinePast;

  // Submitted-at display
  const submittedTime = (existingOrder?.submitted_at || existingOrder?.created_at)
    ? new Date(existingOrder.submitted_at || existingOrder.created_at)
        .toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : null;
  const placedBy = existingOrder?.placed_by || existingOrder?.placedBy || null;

  function row(item) {
    const par = parMap?.[item.id];
    const overPar = par != null && item.quantity > par;
    return (
      <tr key={item.id} style={{ borderBottom: `1px solid ${C.border}`, background: C.white }}>
        <td style={{ padding: '8px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: C.charcoal }}>{item.name}</span>
            {overPar && <span style={{ background: C.amberLight, color: C.amber, borderRadius: '4px', padding: '0.05rem 0.35rem', fontSize: '0.65rem', fontWeight: 700 }}>over par</span>}
          </div>
          {par != null && <div style={{ fontSize: '0.7rem', color: C.mid, marginTop: '2px', paddingLeft: '2px' }}>par {par}</div>}
        </td>
        <td style={{ padding: '8px 10px' }}>
          <span style={{ background: C.skyBlueLight, color: C.skyText, borderRadius: '4px', padding: '0.1rem 0.42rem', fontSize: '0.7rem', fontWeight: 700 }}>{item.unit}</span>
        </td>
        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
          {locked
            ? <span style={{ fontWeight: 700 }}>{item.quantity}</span>
            : <input type='number' min='1' value={item.quantity} onChange={e => onChangeQty(item.id, e.target.value)}
                style={{ width: '56px', padding: '4px', border: `1.5px solid ${overPar ? C.amber : C.border}`, borderRadius: '6px', textAlign: 'center', fontSize: '0.9rem', fontWeight: 700, outline: 'none', color: C.charcoal }} />}
        </td>
        {!locked && <td style={{ padding: '8px 10px', textAlign: 'center' }}><button onClick={() => onRemove(item.id)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center' }}><X size={14} /></button></td>}
      </tr>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} onClick={onClose}>
      <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '580px', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={{ padding: '20px 24px', background: C.navy, borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'flex-start', gap: '14px', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <ShoppingCart size={16} style={{ color: C.yellow }} />
              <span style={{ fontWeight: 800, fontSize: '17px', color: C.yellow }}>Order Cart</span>
              {venue && <span style={{ background: 'rgba(255,255,255,0.12)', color: C.white, padding: '2px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700 }}>{venue}</span>}
              {orderFrom && <span style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', padding: '2px 10px', borderRadius: '999px', fontSize: '11px' }}>from {orderFrom}</span>}
            </div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginTop: '4px' }}>
              {hasItems ? `${cart.length} product${cart.length !== 1 ? 's' : ''} · ${totalUnits} unit${totalUnits !== 1 ? 's' : ''}` : 'No items yet'}
            </div>
            {isEnRoute && (
              <div style={{ marginTop: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>
                Locked — en-route. Go to Transfers to track delivery.
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '6px', display: 'flex', flexShrink: 0 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* ── Submitted banner ── */}
          {isSubmitted && !isEnRoute && (
            deadlinePast ? (
              // Past 2am — locked
              <div style={{ background: C.amberLight, border: `2px solid ${C.amber}60`, borderRadius: '12px', padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
                  <AlertTriangle size={20} style={{ color: C.amber, flexShrink: 0 }} />
                  <span style={{ fontWeight: 800, fontSize: '1.05rem', color: C.amber }}>Order locked — too late to change</span>
                </div>
                <p style={{ margin: '0 0 8px', fontSize: '0.88rem', color: C.charcoal, lineHeight: 1.55 }}>
                  The cut-off has passed. If you need something extra today, submit a new order and set the delivery date manually.
                </p>
                {(placedBy || submittedTime) && (
                  <p style={{ margin: 0, fontSize: '0.75rem', color: C.mid }}>
                    {submittedTime && <>Submitted at {submittedTime}</>}{placedBy && submittedTime && ' · '}{placedBy && <>by {placedBy}</>}
                  </p>
                )}
              </div>
            ) : (
              // Within amendment window — green, clearly tappable
              <div style={{ background: C.greenLight, border: `2px solid ${C.green}`, borderRadius: '12px', padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
                  <CheckCircle size={20} style={{ color: C.green, flexShrink: 0 }} />
                  <span style={{ fontWeight: 800, fontSize: '1.05rem', color: C.green }}>Order submitted ✓</span>
                </div>
                <p style={{ margin: '0 0 10px', fontSize: '0.95rem', color: C.charcoal, lineHeight: 1.55, fontWeight: 500 }}>
                  Need to add or remove something? Change the items below and tap <strong>Update Order</strong>.
                </p>
                {(placedBy || submittedTime) && (
                  <p style={{ margin: '0 0 10px', fontSize: '0.75rem', color: C.mid }}>
                    {submittedTime && <>Submitted at {submittedTime}</>}{placedBy && submittedTime && ' · '}{placedBy && <>by {placedBy}</>}
                  </p>
                )}
                {deadlineLabel && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: C.green, color: C.white, borderRadius: '999px', padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700 }}>
                    <CheckCircle size={12} /> {deadlineLabel}
                  </div>
                )}
              </div>
            )
          )}

          {/* ── Items table ── */}
          {!hasItems ? (
            <div style={{ padding: '3rem 0', textAlign: 'center' }}>
              <ShoppingCart size={36} style={{ color: C.border, marginBottom: '0.75rem' }} />
              <p style={{ color: C.mid, fontSize: '0.9rem', margin: 0, fontWeight: 500 }}>Your cart is empty</p>
              <p style={{ color: C.mid, fontSize: '0.82rem', margin: '0.35rem 0 0' }}>Add products from the list.</p>
            </div>
          ) : (
            <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                    <th style={{ padding: '9px 10px', textAlign: 'left', fontSize: '0.67rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase' }}>Product</th>
                    <th style={{ padding: '9px 10px', textAlign: 'left', fontSize: '0.67rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase' }}>Unit</th>
                    <th style={{ padding: '9px 10px', textAlign: 'center', fontSize: '0.67rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase' }}>Qty</th>
                    {!locked && <th style={{ padding: '9px 10px', width: '32px' }} />}
                  </tr>
                </thead>
                <tbody>
                  {kitItems.length > 0 && (
                    <tr>
                      <td colSpan={locked ? 3 : 4} style={{ padding: '5px 10px 3px', background: C.skyBlueLight, fontSize: '0.65rem', fontWeight: 700, color: C.skyText, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><ChefHat size={10} /> Kitchen</span>
                      </td>
                    </tr>
                  )}
                  {kitItems.map(item => row(item))}
                  {barItems.length > 0 && (
                    <tr>
                      <td colSpan={locked ? 3 : 4} style={{ padding: '5px 10px 3px', background: C.pinkLight, fontSize: '0.65rem', fontWeight: 700, color: C.pinkText, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Coffee size={10} /> Bar</span>
                      </td>
                    </tr>
                  )}
                  {barItems.map(item => row(item))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Notes ── */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.mid, marginBottom: '6px' }}>Notes <span style={{ color: C.border, fontWeight: 400, textTransform: 'none' }}>— optional</span></label>
            <textarea value={notes} onChange={e => onNotesChange(e.target.value)} disabled={locked} placeholder='Extra requests or items not on the list…' rows={2}
              style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${notes.trim() ? C.navy : C.border}`, borderRadius: '8px', fontSize: '13px', color: C.charcoal, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', background: locked ? C.cream : C.white, transition: 'border-color 0.15s' }} />
          </div>

          {/* ── Action buttons ── */}
          {locked ? (
            <button onClick={onClose} style={{ width: '100%', padding: '12px', background: C.navy, color: C.white, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Close</button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {!existingOrder && !allParDone && parRemaining > 0 ? (
                <div style={{ background: '#FFF3E0', border: '1.5px solid rgba(224,123,0,0.4)', borderRadius: '10px', padding: '0.85rem 1rem', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                    <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#E07B00' }}>
                      {parRemaining} item{parRemaining !== 1 ? 's' : ''} still need counting
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#E07B00', opacity: 0.85 }}>
                    Close cart and count remaining stock before submitting
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={onSubmit} disabled={!hasItems || submitting}
                    style={{ flex: 1, padding: '12px', background: hasItems ? C.yellow : C.cream, color: hasItems ? C.charcoal : C.mid, border: 'none', borderRadius: '8px', cursor: hasItems ? 'pointer' : 'default', fontWeight: 800, fontSize: '1rem', transition: 'all 0.15s' }}>
                    {submitting ? 'Submitting…' : isSubmitted ? `Update Order (${cart.length})` : `Submit Order (${cart.length})`}
                  </button>
                  <button onClick={onClose} style={{ padding: '12px 16px', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.white, cursor: 'pointer', fontSize: '13px', color: C.mid, fontWeight: 600 }}>Close</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Coloured Order From pill ──────────────────────────────────────────
function orderFromPill(venue, active) {
  const vi = VENUE_IDENTITY[venue];
  if (vi) {
    return {
      padding: '0.35rem 1rem', borderRadius: '999px',
      border: `2px solid ${active ? vi.border : vi.border + '80'}`,
      background: active ? vi.bg : C.white,
      color: active ? vi.text : C.mid,
      fontSize: '0.83rem', cursor: 'pointer',
      fontWeight: active ? 800 : 500,
      transition: 'all 0.15s ease', whiteSpace: 'nowrap',
      boxShadow: active ? `0 2px 8px ${vi.border}60` : 'none',
    };
  }
  return {
    padding: '0.35rem 1rem', borderRadius: '999px',
    border: `2px solid ${active ? C.pink : C.pink + '60'}`,
    background: active ? C.pink : C.white,
    color: active ? C.pinkText : C.mid,
    fontSize: '0.83rem', cursor: 'pointer',
    fontWeight: active ? 800 : 500,
    transition: 'all 0.15s ease', whiteSpace: 'nowrap',
    boxShadow: active ? `0 2px 8px ${C.pink}60` : 'none',
  };
}

// ── Count Stock modal — embedded stocktake before ordering ────────────
function CountStockModal({ product, par, cartQty, onConfirm, onCancel }) {
  const [count, setCount] = useState('');
  const [editedQty, setEditedQty] = useState(null); // null = use calcQty
  const ref = useRef(null);
  useEffect(() => { if (ref.current) { ref.current.focus(); } }, []);

  const countNum = parseFloat(count);
  const hasCount = count !== '' && !isNaN(countNum);
  const calcQty = hasCount ? Math.max(0, par - countNum) : null;
  const atPar = hasCount && calcQty === 0;
  const isBar = (product.dept || '') === 'bar';

  // Reset editedQty whenever the calculated qty changes (i.e. user edits count)
  useEffect(() => { setEditedQty(null); }, [calcQty]);

  const orderQty = editedQty !== null ? editedQty : (calcQty ?? 0);
  const isAdjusted = editedQty !== null && editedQty !== calcQty;
  const underPar = isAdjusted && orderQty < calcQty;
  const shortfall = underPar ? calcQty - orderQty : 0;

  function confirm() {
    if (!hasCount) return;
    onConfirm({ count: countNum, orderQty });
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.6)', zIndex: 600, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '5vh' }}>
      <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '480px', margin: '0 1rem', padding: '24px 24px 28px', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: isBar ? C.pinkLight : C.skyBlueLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <DeptIcon dept={product.dept || 'kitchen'} size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: C.charcoal }}>{product.name}</div>
            <div style={{ fontSize: '0.78rem', color: C.mid }}>{product.unit} · par level: <strong style={{ color: C.navy }}>{par}</strong>{cartQty > 0 && <span style={{ color: C.navy, fontWeight: 700 }}> · {cartQty} in cart</span>}</div>
          </div>
        </div>

        {/* Count input */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
            Current Stock Level
          </label>
          <input
            ref={ref}
            type="number"
            inputMode="numeric"
            min="0"
            value={count}
            onChange={e => setCount(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && hasCount && confirm()}
            placeholder="Enter current count…"
            style={{ width: '100%', padding: '14px 16px', border: `2px solid ${hasCount ? C.navy : C.border}`, borderRadius: '12px', fontSize: '1.4rem', fontWeight: 800, color: C.navy, outline: 'none', textAlign: 'center', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
          />
        </div>

        {/* Result indicator */}
        {hasCount && (
          <div style={{ background: atPar ? C.greenLight : C.skyBlueLight, border: `1px solid ${atPar ? C.green + '60' : C.skyBlue}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.85rem', color: atPar ? C.green : C.skyText }}>
              {atPar ? 'At or above par — nothing needed' : `Par ${par} − stock ${countNum} =`}
            </span>
            {!atPar && (
              <span style={{ fontWeight: 800, fontSize: '1.1rem', color: C.skyText }}>
                {calcQty} × {product.unit}
              </span>
            )}
            {atPar && <CheckCircle size={18} style={{ color: C.green }} />}
          </div>
        )}

        {/* Primary actions */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: hasCount ? '14px' : 0 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '14px', border: `1.5px solid ${C.border}`, borderRadius: '12px', background: C.white, cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', color: C.mid }}>
            Cancel
          </button>
          <button onClick={confirm} disabled={!hasCount}
            style={{ flex: 2, padding: '14px', background: !hasCount ? C.border : underPar ? C.amber : atPar ? C.green : C.skyText, color: hasCount ? C.white : C.mid, border: 'none', borderRadius: '12px', cursor: hasCount ? 'pointer' : 'default', fontWeight: 800, fontSize: '1rem' }}>
            {!hasCount ? 'Enter count' : atPar && orderQty === 0 ? 'Confirm — at par' : orderQty === 0 ? 'Confirm — skip order' : `Add ${orderQty} to cart`}
          </button>
        </div>

        {/* Secondary — compact adjust strip */}
        {hasCount && (
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>
                {atPar ? 'Add units anyway' : 'Adjust qty'}
              </span>
              {/* − button */}
              <button
                onClick={() => setEditedQty(q => Math.max(0, (q !== null ? q : calcQty) - 1))}
                disabled={orderQty <= 0}
                style={{ width: '32px', height: '32px', borderRadius: '8px', border: `1.5px solid ${orderQty > 0 ? C.border : C.cream}`, background: orderQty > 0 ? C.white : C.cream, color: orderQty > 0 ? C.charcoal : C.mid, fontSize: '1.1rem', fontWeight: 700, cursor: orderQty > 0 ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                −
              </button>
              {/* qty display/input */}
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={orderQty}
                onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 0) setEditedQty(v); }}
                style={{ width: '48px', height: '32px', textAlign: 'center', border: `1.5px solid ${underPar ? C.amber : isAdjusted ? C.navy : C.border}`, borderRadius: '8px', fontSize: '0.95rem', fontWeight: 800, color: underPar ? C.amber : C.navy, outline: 'none', background: underPar ? C.amberLight : isAdjusted ? C.yellowLight : C.cream, boxSizing: 'border-box' }}
              />
              {/* + button */}
              <button
                onClick={() => setEditedQty(q => (q !== null ? q : calcQty) + 1)}
                style={{ width: '32px', height: '32px', borderRadius: '8px', border: `1.5px solid ${C.border}`, background: C.white, color: C.charcoal, fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                +
              </button>
              {/* reset link */}
              {isAdjusted && !atPar && (
                <button onClick={() => setEditedQty(null)} style={{ background: 'none', border: 'none', color: C.mid, fontSize: '0.72rem', cursor: 'pointer', padding: '0 2px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  reset
                </button>
              )}
            </div>
            {/* under-par warning */}
            {underPar && (
              <div style={{ marginTop: '7px', fontSize: '0.73rem', color: C.amber, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <AlertTriangle size={11} style={{ flexShrink: 0 }} />
                {shortfall} {product.unit} short of par
              </div>
            )}
            {orderQty === 0 && !atPar && (
              <div style={{ marginTop: '7px', fontSize: '0.73rem', color: C.mid, fontWeight: 600 }}>
                Count recorded — nothing ordered
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ── Quantity modal — regular add on mobile ────────────────────────────
function QuantityModal({ product, inCart, cartQty, parMap, onConfirm, onCancel }) {
  const par = parMap?.[product.id];
  const suggested = par != null ? Math.max(0, par - (cartQty || 0)) : 1;
  const [qty, setQty] = useState(suggested > 0 ? suggested : 1);
  const ref = useRef(null);
  useEffect(() => { if (ref.current) { ref.current.focus(); ref.current.select(); } }, []);

  const overPar = par != null && (qty + (cartQty || 0)) > par;
  const isBar = (product.dept || '') === 'bar';

  function confirm() { if (qty > 0) onConfirm(qty); }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.6)', zIndex: 600, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '5vh' }}>
      <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '400px', margin: '0 1rem', padding: '24px 24px 28px', boxShadow: '0 8px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: C.border, margin: '0 auto 20px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: isBar ? C.pinkLight : C.skyBlueLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <DeptIcon dept={product.dept || 'kitchen'} size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: C.charcoal }}>{product.name}</div>
            <div style={{ fontSize: '0.78rem', color: C.mid }}>
              {product.unit}
              {par != null && <span> · par {par}</span>}
              {cartQty > 0 && <span style={{ color: C.navy, fontWeight: 700 }}> · {cartQty} in cart</span>}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => setQty(q => Math.max(1, q - 1))}
            style={{ width: 52, height: 52, borderRadius: 14, border: `2px solid ${C.border}`, background: qty > 1 ? C.navy : C.cream, color: qty > 1 ? C.white : C.mid, fontSize: '1.5rem', fontWeight: 700, cursor: qty > 1 ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            −
          </button>
          <input
            ref={ref}
            type="number" inputMode="numeric" pattern="[0-9]*"
            value={qty}
            onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v >= 0) setQty(v); }}
            onKeyDown={e => e.key === 'Enter' && confirm()}
            style={{ width: 80, height: 52, textAlign: 'center', border: `2px solid ${overPar ? C.amber : C.navy}`, borderRadius: 14, fontSize: '1.6rem', fontWeight: 800, color: overPar ? C.amber : C.navy, outline: 'none', background: overPar ? C.amberLight : C.yellowLight }}
          />
          <button onClick={() => setQty(q => q + 1)}
            style={{ width: 52, height: 52, borderRadius: 14, border: `2px solid ${C.navy}`, background: C.navy, color: C.white, fontSize: '1.5rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            +
          </button>
        </div>

        {overPar && (
          <div style={{ textAlign: 'center', fontSize: '0.78rem', color: C.amber, fontWeight: 700, marginBottom: 16 }}>
            Over par — ordering {qty + (cartQty || 0)} vs par {par}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '14px', border: `1.5px solid ${C.border}`, borderRadius: 12, background: C.white, cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', color: C.mid }}>
            Cancel
          </button>
          <button onClick={confirm} disabled={qty <= 0}
            style={{ flex: 2, padding: '14px', background: qty > 0 ? (inCart ? C.green : C.charcoal) : C.border, color: qty > 0 ? C.white : C.mid, border: 'none', borderRadius: 12, cursor: qty > 0 ? 'pointer' : 'default', fontWeight: 800, fontSize: '1rem' }}>
            {inCart ? `+ Add ${qty} more` : `Add ${qty} to cart`}
          </button>
        </div>
      </div>
    </div>
  );
}

function CartFAB({ count, onClick }) {
  return (
    <button onClick={onClick} style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 300, width: '64px', height: '64px', borderRadius: '50%', background: count > 0 ? C.navy : C.mid, color: C.white, border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(44,58,66,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', transition: 'background 0.2s, transform 0.15s', WebkitTapHighlightColor: 'transparent' }}
      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.07)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
      <ShoppingCart size={22} />
      {count > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-2px', background: C.yellow, color: C.charcoal, borderRadius: '999px', fontSize: '0.72rem', fontWeight: 800, minWidth: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', border: `2px solid ${C.white}`, lineHeight: 1 }}>{count}</span>}
    </button>
  );
}

// ── Product row — clean card, no highlights, icon-based dept ─────────
function ProductRow({ product, inCart, cartQty, onAddToCart, isLast, parMap, parCountsDone, onParCount, isDupWarning, isMobile, isAdmin }) {
  const [showQtyModal, setShowQtyModal] = useState(false);
  const [showCountModal, setShowCountModal] = useState(false);
  const isBar = (product.dept || '') === 'bar';
  const par = parMap?.[product.id];
  // Admins bypass the count gate; managers and kitchen/bar staff must count
  const requiresCount = par != null && !parCountsDone[product.id] && !isAdmin;
  const gap = par != null && parCountsDone[product.id] != null
    ? Math.max(0, par - (cartQty || 0))
    : null;

  function handleMobileAdd(confirmedQty) { onAddToCart(product, confirmedQty); setShowQtyModal(false); }
  function handleCountConfirm({ count, orderQty }) {
    onParCount(product.id, count);
    if (orderQty > 0) onAddToCart(product, orderQty);
    setShowCountModal(false);
  }

  // Desktop add — direct stepper not used on mobile; skip for brevity, use modal pattern for both
  function handleAdd() {
    if (requiresCount) { setShowCountModal(true); return; }
    setShowQtyModal(true);
  }

  return (
    <>
      {showCountModal && (
        <CountStockModal
          product={product}
          par={par}
          cartQty={cartQty}
          onConfirm={handleCountConfirm}
          onCancel={() => setShowCountModal(false)}
        />
      )}
      {showQtyModal && (
        <QuantityModal
          product={product}
          inCart={inCart}
          cartQty={cartQty}
          parMap={parMap}
          onConfirm={handleMobileAdd}
          onCancel={() => setShowQtyModal(false)}
        />
      )}

      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '0.75rem' : '0.65rem 0.85rem',
        borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
        gap: '0.65rem',
        background: C.white,
        transition: 'background 0.15s',
      }}>
        {/* Dept icon — minimal, no coloured left border */}
        <div style={{ width: 32, height: 32, borderRadius: 8, background: isBar ? C.pinkLight : C.orangeLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <DeptIcon dept={product.dept || 'kitchen'} size={14} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: isMobile ? '0.95rem' : '0.9rem', color: C.charcoal }}>{product.name}</span>
            {isDupWarning && <span style={{ background: C.redLight, color: C.red, borderRadius: '4px', padding: '0.05rem 0.35rem', fontSize: '0.65rem', fontWeight: 700 }}>already in cart</span>}
          </div>
          <div style={{ fontSize: '0.72rem', color: C.mid, marginTop: '0.1rem', display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
            <span style={{ background: C.skyBlueLight, color: C.skyText, borderRadius: '4px', padding: '0.08rem 0.4rem', fontSize: '0.68rem', fontWeight: 700 }}>{product.unit}</span>
            {par != null && requiresCount && <span style={{ color: C.green, fontWeight: 600 }}>count required · par {par}</span>}
            {par != null && !requiresCount && gap != null && gap > 0 && <span style={{ color: C.green, fontWeight: 600 }}>need {gap}</span>}
            {par != null && !requiresCount && gap === 0 && <span style={{ color: C.green, fontWeight: 600 }}>✓ at par</span>}
            {inCart && cartQty > 0 && !requiresCount && <span style={{ color: C.navy, fontWeight: 600 }}>{cartQty} in cart</span>}
          </div>
        </div>

        {/* Action button */}
        {requiresCount ? (
          <button onClick={() => setShowCountModal(true)}
            style={{ padding: '0 0.9rem', height: '36px', background: C.amberLight, color: C.amber, border: `1.5px solid ${C.amber}60`, borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '5px', WebkitTapHighlightColor: 'transparent' }}>
            <ClipboardList size={12} />
            Count
          </button>
        ) : (
          <button onClick={handleAdd}
            style={{ padding: '0 1rem', height: '36px', background: inCart ? C.green : C.charcoal, color: C.white, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0, minWidth: '64px', WebkitTapHighlightColor: 'transparent' }}>
            {inCart ? `✓ ${cartQty}` : '+ Add'}
          </button>
        )}
      </div>
    </>
  );
}

function ExternalOrderCard({ req, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const accent = req.status === 'pending' ? C.orange : C.green;
  async function markOrdered() {
    if (!confirmName.trim()) return; setSaving(true);
    const { data, error } = await supabase.from('requests').update({ status: 'confirmed', placed_by: confirmName.trim().toUpperCase() }).eq('id', req.id).select().single();
    if (!error && data) onUpdate({ ...data, orderFrom: data.order_from, placedBy: data.placed_by });
    setSaving(false); setConfirming(false); setConfirmName('');
  }
  async function deleteOrder() { await supabase.from('requests').delete().eq('id', req.id); onUpdate(null); }
  return (
    <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, borderLeft: `4px solid ${accent}`, marginBottom: '0.5rem', overflow: 'hidden' }}>
      <div onClick={() => setExpanded(e => !e)} style={{ padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.65rem', cursor: 'pointer' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: C.charcoal }}>{req.venue} Order</div>
          <div style={{ fontSize: '0.72rem', color: C.mid }}>{req.orderFrom || req.order_from} · {fmtDate(req.date)} · {(req.items || []).length} items{req.delivery_date ? ` · delivery ${fmtDate(req.delivery_date)}` : ''}</div>
        </div>
        <StatusPill status={req.status} />
        <span style={{ color: C.mid, fontSize: '0.8rem' }}>{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '0.85rem 1rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '0.85rem' }}>
            <thead><tr style={{ background: C.cream }}>{['Dept', 'Product', 'Unit', 'Qty'].map(h => <th key={h} style={{ padding: '0.4rem 0.65rem', textAlign: 'left', fontSize: '0.67rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
            <tbody>
              {(req.items || []).map((item, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '0.5rem 0.65rem' }}><DeptBadge dept={item.dept || 'kitchen'} /></td>
                  <td style={{ padding: '0.5rem 0.65rem', fontWeight: 600, color: C.charcoal }}>{item.name}</td>
                  <td style={{ padding: '0.5rem 0.65rem', color: C.mid }}>{item.unit}</td>
                  <td style={{ padding: '0.5rem 0.65rem', fontWeight: 700, color: C.charcoal }}>{item.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {req.status === 'pending' && (confirming ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap', background: C.cream, padding: '0.75rem', borderRadius: '8px' }}>
              <input value={confirmName} onChange={e => setConfirmName(e.target.value.toUpperCase())} placeholder='Initials' maxLength={4} style={{ padding: '0.4rem 0.75rem', border: `1.5px solid ${C.border}`, borderRadius: '6px', fontSize: '1rem', fontWeight: 700, width: '90px', outline: 'none', color: C.charcoal, letterSpacing: '0.1em', textAlign: 'center' }} />
              <button onClick={markOrdered} disabled={!confirmName.trim() || saving} style={{ padding: '0.45rem 1.1rem', background: confirmName.trim() ? C.green : '#ccc', color: C.white, border: 'none', borderRadius: '6px', cursor: confirmName.trim() ? 'pointer' : 'default', fontWeight: 700, fontSize: '0.85rem' }}>{saving ? 'Saving…' : '✓ Mark Ordered'}</button>
              <button onClick={() => setConfirming(false)} style={{ padding: '0.45rem 0.85rem', border: `1.5px solid ${C.border}`, borderRadius: '6px', background: C.white, cursor: 'pointer', fontSize: '0.85rem', color: C.mid }}>Cancel</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.65rem' }}>
              <button onClick={() => setConfirming(true)} style={{ padding: '0.45rem 1.1rem', background: C.green, color: C.white, border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>✓ Mark as Ordered</button>
              <button onClick={deleteOrder} style={{ padding: '0.45rem 0.85rem', border: `1.5px solid ${C.red}60`, borderRadius: '6px', background: C.white, cursor: 'pointer', fontSize: '0.85rem', color: C.red, fontWeight: 600 }}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────
export default function Orders({ requests, setRequests, currentUser, products, prefilledCart, setPrefilledCart, parMap, onNavigate, onCartCountChange }) {
  const isAdmin = isAdminUser(currentUser);
  const isPK = currentUser?.venue === 'Prep Kitchen';
  const isMobile = useIsMobile();
  const userVenue = currentUser?.venue || '';
  const DEPT_ROLES = ['kitchen', 'bar'];
  const userDept = DEPT_ROLES.includes(currentUser?.role) ? currentUser.role : null;
  const SUPERVISOR_ROLES = ['supervisor', 'manager', 'admin'];
  const isSupervisorPlus = isAdmin || SUPERVISOR_ROLES.includes(currentUser?.role);

  if (isPK && !isAdmin) return (
    <div style={{ padding: '3rem', textAlign: 'center', color: C.mid, fontFamily: 'system-ui, sans-serif' }}>
      <UtensilsCrossed size={32} style={{ color: C.navy, marginBottom: '1rem', opacity: 0.4 }} />
      <p style={{ fontWeight: 600, color: C.navy }}>Orders not available for Prep Kitchen</p>
      <p style={{ fontSize: '0.85rem' }}>Use the Transfers page to fulfil incoming orders.</p>
    </div>
  );

  const opDate = getOpDate();
  const deliveryDate = getDeliveryDate(opDate);

  function rowDept(row) {
    const items = row.items || [];
    if (!items.length) return 'kitchen';
    const allBar = items.every(i => (i.dept || '').toLowerCase() === 'bar');
    return allBar ? 'bar' : 'kitchen';
  }
  // sessionDept defined below after activeDept state

  // Generic shared logins — must still enter initials manually
  const isGenericUser = currentUser?.is_generic === true;

  // For named users, sign with their full name directly
  const userAutoInitials = !isGenericUser && currentUser?.name
    ? currentUser.name
    : null;

  const [activeTab, setActiveTab] = useState('internal');
  const [venue, setVenue] = useState(userVenue);
  const [orderFrom, setOrderFrom] = useState('Prep Kitchen');
  const [activeCat, setActiveCat] = useState('All');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showLemon, setShowLemon] = useState(false);
  const [showInitials, setShowInitials] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warnOver, setWarnOver] = useState([]);
  const [warnDup, setWarnDup] = useState([]);
  const [cartDupWarning, setCartDupWarning] = useState(null);
  const [orderNotes, setOrderNotes] = useState('');
  // parCountsDone: { [productId]: countValue } — tracks which par products have been counted this session
  const [parCountsDone, setParCountsDone] = useState({});
  // activeDept: for supervisor+ toggle between bar/kitchen views. null = no filter (admins). Supervisors default 'bar'.
  const [activeDept, setActiveDept] = useState(isSupervisorPlus && !isAdmin ? 'bar' : null);
  const sessionDept = userDept || (isSupervisorPlus && activeDept) || 'kitchen';
  const pendingInits = useRef('');

  function lemon() { setShowLemon(true); setTimeout(() => setShowLemon(false), 1400); }

  // Warn on browser close/refresh if cart has items
  useEffect(() => {
    function handleBeforeUnload(e) {
      if (cart.length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [cart]);

  // Intercept in-app navigation if cart has items.
  // safeNavigate is passed to any nav element rendered inside Orders.
  // For App.js sidebar nav, App.js should call this instead of its own navigate when Orders is active.
  function safeNavigate(page) {
    if (cart.length > 0) {
      const ok = window.confirm(`You have ${cart.length} item${cart.length !== 1 ? 's' : ''} in your cart that haven't been submitted. Your draft is saved — come back to finish it.`);
      if (!ok) return;
    }
    onNavigate(page);
  }

  useEffect(() => { supabase.from('categories').select('*').eq('active', true).order('name').then(({ data }) => { if (data) setCategories(data); }); }, []);

  const cartInitialised = useRef(false);

  // Reset par counts and cart initialisation flag when venue/orderFrom changes
  useEffect(() => {
    setParCountsDone({});
    cartInitialised.current = false;
  }, [venue, orderFrom]);

  // Auto-save draft to localStorage whenever cart changes (only if cart is non-empty)
  useEffect(() => {
    if (!venue || !orderFrom || !cartInitialised.current) return;
    if (cart.length > 0) {
      saveCartDraft(venue, orderFrom, { cart, orderNotes, parCountsDone });
    }
  }, [cart, orderNotes, parCountsDone]);

  // Report cart count to App so Sidebar can show leave warning
  useEffect(() => {
    if (onCartCountChange) onCartCountChange(cart.length);
  }, [cart.length]);

  // Load today's existing order into cart — only once per venue/orderFrom selection.
  // Match by dept so bar and kitchen each load their own row.
  useEffect(() => {
    if (!venue || !orderFrom || cartInitialised.current) return;
    const candidates = requests.filter(r =>
      r.venue === venue && r.date === opDate &&
      r.source !== 'stocktake_external' &&
      INTERNAL_VENUES.includes(r.orderFrom || r.order_from) &&
      (r.orderFrom || r.order_from) === orderFrom &&
      r.status !== 'received'
    );
    const existing = candidates.find(r => rowDept(r) === sessionDept) || candidates[0] || null;
    if (existing) {
      // Check if amendment window has closed (2am on delivery date)
      const deliveryDate = existing.delivery_date;
      const deadlinePast = deliveryDate
        ? new Date() > new Date(deliveryDate + 'T02:00:00')
        : false;
      if (deadlinePast) {
        // Order locked — start fresh, clear any stale draft
        clearCartDraft(venue, orderFrom);
        setCart([]);
        setOrderNotes('');
      } else {
        // DB submitted order takes priority — clear any stale draft
        setCart(existing.items || []);
        setOrderNotes(existing.notes || '');
        clearCartDraft(venue, orderFrom);
      }
    } else {
      // No submitted order — restore localStorage draft if present
      const draft = loadCartDraft(venue, orderFrom);
      if (draft) {
        setCart(draft.cart || []);
        setOrderNotes(draft.orderNotes || '');
        setParCountsDone(draft.parCountsDone || {});
      } else {
        setCart([]);
        setOrderNotes('');
      }
    }
    cartInitialised.current = true;
  }, [venue, orderFrom, requests]);

  useEffect(() => {
    if (!prefilledCart) return;
    if (prefilledCart.type === 'internal') { setActiveTab('internal'); setVenue(prefilledCart.venue || userVenue); setOrderFrom('Prep Kitchen'); setCart(prefilledCart.items || []); }
    else if (prefilledCart.type === 'external') setActiveTab('external');
  }, [prefilledCart, userVenue]);

  // Dept filter: kitchen/bar roles locked to their dept; supervisor+ use activeDept toggle (null = all for admins)
  const deptFilter = userDept ? userDept : (isSupervisorPlus && activeDept) ? activeDept : null;
  const baseProd = products
    .filter(p => p.active)
    .filter(p => { if (!orderFrom) return false; const at = p.availableAt || p.available_at || []; const fr = p.orderFromVenues || p.order_from_venues || []; return at.includes(venue) && fr.includes(orderFrom); })
    .filter(p => { const ft = p.fulfilmentType || p.fulfilment_type || 'external'; const df = p.defaultFulfiller || p.default_fulfiller || ''; return ft === 'internal' || (ft === 'both' && df !== venue); })
    .filter(p => { if (!deptFilter) return true; return (p.dept || '').toLowerCase() === deptFilter.toLowerCase(); });

  const catNames = [...new Set(baseProd.map(p => p.category).filter(Boolean))];
  const filtProd = baseProd.filter(p => activeCat === 'All' || p.category === activeCat).filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));

  // Split: par products needing count come first, rest below
  const parProducts = filtProd.filter(p => parMap?.[p.id] != null && !parCountsDone[p.id]);
  const remainingProducts = filtProd.filter(p => !(parMap?.[p.id] != null && !parCountsDone[p.id]));

  // Count of outstanding par counts
  const totalParRequired = baseProd.filter(p => parMap?.[p.id] != null).length;
  const totalParDone = Object.keys(parCountsDone).filter(id => baseProd.find(p => p.id === id && parMap?.[p.id] != null)).length;
  // Admins bypass the par gate; everyone else (including supervisors on a dept view) must complete counts
  const allParDone = isAdmin || totalParRequired === 0 || totalParDone >= totalParRequired;

  const existingOrderCandidates = requests.filter(r =>
    r.venue === venue && r.date === opDate &&
    r.source !== 'stocktake_external' &&
    INTERNAL_VENUES.includes(r.orderFrom || r.order_from) &&
    (r.orderFrom || r.order_from) === orderFrom &&
    r.status !== 'received'
  );
  const existingOrder = existingOrderCandidates.find(r => rowDept(r) === sessionDept) || existingOrderCandidates[0] || null;
  const isEnRoute = existingOrder?.status === 'en-route' || existingOrder?.status === 'partially fulfilled';

  const extAll = requests.filter(r => r.source === 'stocktake_external');
  const extPend = extAll.filter(r => r.status === 'pending');
  const extConf = extAll.filter(r => r.status !== 'pending');
  const bySup = extPend.reduce((acc, r) => { const s = r.orderFrom || r.order_from || 'No Supplier'; if (!acc[s]) acc[s] = []; acc[s].push(r); return acc; }, {});
  const ofOpts = venue ? ['Prep Kitchen', ...ALL_VENUES.filter(v => v !== venue)] : ['Prep Kitchen', ...ALL_VENUES];

  function addToCart(product, qty) {
    const existing = cart.find(i => i.id === product.id);
    if (existing) { setCartDupWarning(product.id); setTimeout(() => setCartDupWarning(null), 3000); }
    setCart(prev => { const ex = prev.find(i => i.id === product.id); if (ex) return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + qty } : i); return [...prev, { ...product, quantity: qty }]; });
  }
  function rmFromCart(id) { setCart(prev => prev.filter(i => i.id !== id)); }
  function chgCartQty(id, val) { const q = parseInt(val, 10); if (q > 0) setCart(prev => prev.map(i => i.id === id ? { ...i, quantity: q } : i)); }
  function chgVenue(v) {
    if (!isAdmin && currentUser?.role !== 'manager') return;
    if (v === venue) return;
    if (cart.length > 0) {
      const ok = window.confirm(`You have ${cart.length} item${cart.length !== 1 ? 's' : ''} in your cart for ${venue}. They'll be saved and waiting when you come back.`);
      if (!ok) return;
    }
    saveCartDraft(venue, orderFrom, { cart, orderNotes, parCountsDone });
    const draft = loadCartDraft(v, orderFrom);
    setVenue(v);
    setCart(draft?.cart || []);
    setOrderNotes(draft?.orderNotes || '');
    setParCountsDone(draft?.parCountsDone || {});
    cartInitialised.current = false;
  }

  function handleParCount(productId, count) {
    setParCountsDone(prev => ({ ...prev, [productId]: count }));
  }

  function changeDept(dept) {
    if (dept === activeDept) return;
    if (cart.length > 0) {
      const ok = window.confirm(`You have ${cart.length} item${cart.length !== 1 ? 's' : ''} in your cart for the ${activeDept} order. They'll be saved and waiting when you come back.`);
      if (!ok) return;
    }
    saveCartDraft(venue, orderFrom + '_' + activeDept, { cart, orderNotes, parCountsDone });
    const draft = loadCartDraft(venue, orderFrom + '_' + dept);
    setActiveDept(dept);
    setCart(draft?.cart || []);
    setOrderNotes(draft?.orderNotes || '');
    setParCountsDone(draft?.parCountsDone || {});
    setActiveCat('All');
    cartInitialised.current = false;
  }

  function onSubmitClick() {
    if (!venue || !orderFrom || !cart.length) return;
    if (!existingOrder && !allParDone) return;
    setShowCart(false);
    const over = cart.filter(item => { const p = parMap?.[item.id]; return p && item.quantity > p; }).map(item => ({ ...item, par: parMap[item.id] }));
    const dup = isEnRoute
      ? (() => {
          const existingItems = existingOrder ? (existingOrder.items || []) : [];
          const existingNames = new Set(existingItems.map(i => i.name?.toLowerCase().trim()).filter(Boolean));
          return cart.filter(i => i.name && existingNames.has(i.name.toLowerCase().trim()));
        })()
      : [];
    if (over.length > 0 || dup.length > 0) { setWarnOver(over); setWarnDup(dup); setShowWarning(true); return; }
    if (userAutoInitials) { doSubmit(userAutoInitials); return; }
    setShowInitials(true);
  }
  function onInitials(init) { setShowInitials(false); pendingInits.current = init; doSubmit(init); }
  function onWarnConfirm() { setShowWarning(false); if (userAutoInitials) { doSubmit(userAutoInitials); } else { setShowInitials(true); } }

  async function doSubmit(init) {
    setSubmitting(true);
    const { data: freshRows } = await supabase.from('requests').select('*')
      .eq('venue', venue).eq('date', opDate).eq('status', 'pending')
      .eq('order_from', orderFrom).neq('source', 'stocktake_external');
    const existingRow = (freshRows || []).find(r => rowDept(r) === sessionDept)
      || (freshRows || [])[0]
      || null;
    if (existingRow && rowDept(existingRow) === sessionDept) {
      const { data, error } = await supabase.from('requests').update({ items: cart, placed_by: init, delivery_date: deliveryDate, notes: orderNotes.trim() || null }).eq('id', existingRow.id).select().single();
      if (!error && data) {
        const updated = { ...data, orderFrom: data.order_from, placedBy: data.placed_by };
        setRequests(prev => prev.map(r => r.id === existingRow.id ? updated : r));
        cartInitialised.current = true;
      }
    } else {
      const { data, error } = await supabase.from('requests').insert({ venue, order_from: orderFrom, placed_by: init, date: opDate, delivery_date: deliveryDate, status: 'pending', items: cart, source: 'manual', notes: orderNotes.trim() || null }).select().single();
      if (!error && data) {
        setRequests(prev => [{ ...data, orderFrom: data.order_from, placedBy: data.placed_by }, ...prev]);
        cartInitialised.current = true;
      }
    }
    if (setPrefilledCart) setPrefilledCart(null);
    clearCartDraft(venue, orderFrom);
    setCart([]);
    lemon(); setSubmitting(false);
  }

  const fromST = prefilledCart?.fromStocktake === true;

  return (
    <div style={{ padding: isMobile ? '0.75rem' : '1.5rem', maxWidth: '1280px', margin: '0 auto', fontFamily: 'system-ui, sans-serif', minHeight: '100vh', paddingBottom: '6rem' }}>
      {showLemon && <LemonBurst />}
      {showInitials && <InitialsModal onConfirm={onInitials} onCancel={() => setShowInitials(false)} />}
      {showWarning && <SubmitWarningModal overages={warnOver} duplicates={warnDup} onConfirm={onWarnConfirm} onCancel={() => setShowWarning(false)} />}
      {showCart && <CartModal cart={cart} orderFrom={orderFrom} venue={venue} onClose={() => setShowCart(false)} onRemove={rmFromCart} onChangeQty={chgCartQty} onSubmit={onSubmitClick} submitting={submitting} parMap={parMap} notes={orderNotes} onNotesChange={setOrderNotes} existingOrder={existingOrder} allParDone={allParDone} parRemaining={totalParRequired - totalParDone} />}
      {activeTab === 'internal' && <CartFAB count={cart.length} onClick={() => setShowCart(true)} />}

      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: '0 0 0.2rem', fontSize: '1.5rem', fontWeight: 800, color: C.navy, letterSpacing: '-0.02em' }}>Orders</h2>
        <p style={{ margin: 0, fontSize: '0.85rem', color: C.mid }}>Internal and external orders</p>
      </div>

      {fromST && (
        <div style={{ background: C.yellowLight, border: `1.5px solid ${C.yellowDark}60`, borderRadius: '10px', padding: '0.85rem 1.1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.65rem' }}>
          <ClipboardList size={18} style={{ color: C.yellowDark, flexShrink: 0, marginTop: '1px' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '0.9rem', color: C.charcoal, marginBottom: '0.2rem' }}>Stocktake submitted — review your order</div>
            <div style={{ fontSize: '0.8rem', color: C.mid }}>Cart pre-filled from stocktake. Open cart to review quantities, then submit.</div>
          </div>
          <button onClick={() => { if (setPrefilledCart) setPrefilledCart(null); }} style={{ background: 'none', border: 'none', color: C.mid, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center' }}><X size={16} /></button>
        </div>
      )}

      <div style={{ display: 'flex', borderBottom: `1.5px solid ${C.border}`, marginBottom: '1.75rem' }}>
        {[{ key: 'internal', label: 'Internal Orders' }, { key: 'external', label: 'External Orders', count: extPend.length }].map(({ key, label, count }) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{ padding: '0.6rem 1.4rem', borderRadius: '8px 8px 0 0', border: '1.5px solid', borderColor: activeTab === key ? C.border : 'transparent', borderBottom: activeTab === key ? `1.5px solid ${C.white}` : `1.5px solid ${C.border}`, background: activeTab === key ? C.white : 'transparent', color: activeTab === key ? C.navy : C.mid, fontSize: '0.9rem', cursor: 'pointer', fontWeight: activeTab === key ? 700 : 500, marginBottom: '-1.5px', transition: 'all 0.15s' }}>
            {label}{count > 0 && <span style={{ background: C.green, color: C.white, borderRadius: '999px', fontSize: '0.7rem', fontWeight: 800, padding: '0.1rem 0.5rem', marginLeft: '0.5rem' }}>{count}</span>}
          </button>
        ))}
      </div>

      {activeTab === 'internal' && (
        <>
          {(isAdmin || (currentUser?.role === 'manager' && getUserVenues(currentUser).length > 1)) && (
            <div style={{ marginBottom: '1.25rem' }}>
              <p style={SL}>Ordering Venue</p>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {(isAdmin ? ALL_VENUES : getUserVenues(currentUser).filter(v => ALL_VENUES.includes(v))).map(v => (
                  <button key={v} onClick={() => chgVenue(v)} style={pill(venue === v)}>{v}</button>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginBottom: '1.25rem' }}>
            <p style={SL}>Order From</p>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {ofOpts.map(o => <button key={o} onClick={() => {
                if (o === orderFrom) return;
                if (cart.length > 0) {
                  const ok = window.confirm(`You have ${cart.length} item${cart.length !== 1 ? 's' : ''} in your cart for ${orderFrom}. They'll be saved and waiting when you come back.`);
                  if (!ok) return;
                }
                saveCartDraft(venue, orderFrom, { cart, orderNotes, parCountsDone });
                const draft = loadCartDraft(venue, o);
                setOrderFrom(o);
                setCart(draft?.cart || []);
                setOrderNotes(draft?.orderNotes || '');
                setParCountsDone(draft?.parCountsDone || {});
                setActiveCat('All');
                cartInitialised.current = false;
              }} style={orderFromPill(o, orderFrom === o)}>{o}</button>)}
            </div>
          </div>
          {isEnRoute && (
            <div style={{ background: C.skyBlueLight, border: `1px solid ${C.skyBlue}60`, borderRadius: '8px', padding: '0.65rem 1rem', marginBottom: '1.1rem', fontSize: '0.83rem', color: C.skyText, fontWeight: 600 }}>
              This order is en-route — it's locked. You can still add items to create a supplementary order.
            </div>
          )}

          {/* Par count progress banner — only for new orders, not edits */}
          {totalParRequired > 0 && !search && !existingOrder && (
            <div style={{ background: allParDone ? C.greenLight : C.amberLight, border: `1px solid ${allParDone ? C.green + '60' : C.amber + '60'}`, borderRadius: '8px', padding: '0.65rem 1rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {allParDone
                    ? <CheckCircle size={15} style={{ color: C.green, flexShrink: 0 }} />
                    : <ClipboardList size={15} style={{ color: C.amber, flexShrink: 0 }} />}
                  <span style={{ fontSize: '0.83rem', fontWeight: 600, color: allParDone ? C.green : C.amber }}>
                    {allParDone ? 'All counts done — you can submit' : `Stock count required before ordering`}
                  </span>
                </div>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: allParDone ? C.green : C.amber, whiteSpace: 'nowrap' }}>
                  {totalParDone}/{totalParRequired}
                </span>
              </div>
              {!allParDone && (
                <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(224,123,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.75rem', color: C.amber }}>
                    {totalParRequired - totalParDone} item{totalParRequired - totalParDone !== 1 ? 's' : ''} remaining
                  </span>
                  <button
                    onClick={() => {
                      const newCounts = {};
                      baseProd.forEach(p => {
                        if (parMap?.[p.id] != null && parCountsDone[p.id] === undefined) {
                          newCounts[p.id] = parMap[p.id];
                        }
                      });
                      setParCountsDone(prev => ({ ...prev, ...newCounts }));
                    }}
                    style={{ padding: '0.28rem 0.85rem', background: '#fff', border: '1.5px solid #E07B00', borderRadius: '999px', color: '#E07B00', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Mark all remaining at par
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Bar / Kitchen dept toggle — supervisor+ only */}
          {isSupervisorPlus && !userDept && (
            <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.85rem' }}>
              {['bar', 'kitchen'].map(dept => {
                const isBar = dept === 'bar';
                const active = activeDept === dept;
                return (
                  <button key={dept} onClick={() => changeDept(dept)}
                    style={{ padding: '0.35rem 1.1rem', borderRadius: '999px', border: `1.5px solid ${active ? (isBar ? C.pinkText : C.orange) : C.border}`, background: active ? (isBar ? C.pinkLight : C.orangeLight) : C.white, color: active ? (isBar ? C.pinkText : C.orange) : C.mid, fontSize: '0.83rem', cursor: 'pointer', fontWeight: active ? 800 : 500, transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {isBar ? <Coffee size={13} /> : <ChefHat size={13} />}
                    {isBar ? 'Bar' : 'Kitchen'}
                  </button>
                );
              })}
              {isAdmin && (
                <button onClick={() => { if (cart.length > 0) { const ok = window.confirm('Cart will be cleared when viewing all items.'); if (!ok) return; } setActiveDept(null); setCart([]); setParCountsDone({}); setActiveCat('All'); }}
                  style={{ padding: '0.35rem 1.1rem', borderRadius: '999px', border: `1.5px solid ${activeDept === null ? C.navy : C.border}`, background: activeDept === null ? C.navy : C.white, color: activeDept === null ? C.white : C.mid, fontSize: '0.83rem', cursor: 'pointer', fontWeight: activeDept === null ? 800 : 500, transition: 'all 0.15s ease' }}>
                  All
                </button>
              )}
            </div>
          )}

          <div style={{ marginBottom: '0.85rem' }}>
            <input value={search} onChange={e => { setSearch(e.target.value); setActiveCat('All'); }} placeholder='Search products…' style={{ width: '100%', padding: '0.55rem 0.9rem', border: `1.5px solid ${search ? C.navy : C.border}`, borderRadius: '8px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', background: C.white, color: C.charcoal, transition: 'border-color 0.15s' }} />
          </div>
          {!search && (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.85rem' }}>
              <button onClick={() => setActiveCat('All')} style={categoryPill(activeCat === 'All')}>All</button>
              {categories.filter(c => catNames.includes(c.name)).map(c => <button key={c.name} onClick={() => setActiveCat(c.name)} style={categoryPill(activeCat === c.name)}>{c.name}</button>)}
            </div>
          )}

          <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            {!orderFrom
              ? <p style={{ padding: '1.25rem', color: C.mid, fontSize: '0.88rem' }}>Select a source to see products.</p>
              : filtProd.length === 0
              ? <p style={{ padding: '1.25rem', color: C.mid, fontSize: '0.88rem' }}>No products match this selection.</p>
              : (
                <>
                  {/* Par products that need counting — pinned to top, new orders only */}
                  {parProducts.length > 0 && !search && !existingOrder && (
                    <>
                      <div style={{ padding: '0.4rem 0.85rem', background: C.greenLight, borderBottom: `1px solid ${C.green}30`, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <ClipboardList size={11} style={{ color: C.green }} />
                        <span style={{ fontSize: '0.67rem', fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Count required</span>
                      </div>
                      {parProducts.map((p, i) => {
                        const ci = cart.find(c => c.id === p.id);
                        return <ProductRow key={p.id} product={p} inCart={!!ci} cartQty={ci?.quantity || 0} onAddToCart={addToCart} isLast={i === parProducts.length - 1 && remainingProducts.length === 0} parMap={parMap} parCountsDone={parCountsDone} onParCount={handleParCount} isDupWarning={cartDupWarning === p.id} isMobile={isMobile} isAdmin={isAdmin} />;
                      })}
                    </>
                  )}

                  {/* Remaining products (or all products when editing) */}
                  {(existingOrder ? filtProd : remainingProducts).length > 0 && (
                    <>
                      {parProducts.length > 0 && !search && !existingOrder && (
                        <div style={{ padding: '0.4rem 0.85rem', background: C.cream, borderBottom: `1px solid ${C.border}`, borderTop: `1px solid ${C.border}` }}>
                          <span style={{ fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Products</span>
                        </div>
                      )}
                      {(existingOrder ? filtProd : remainingProducts).map((p, i) => {
                        const list = existingOrder ? filtProd : remainingProducts;
                        const ci = cart.find(c => c.id === p.id);
                        return <ProductRow key={p.id} product={p} inCart={!!ci} cartQty={ci?.quantity || 0} onAddToCart={addToCart} isLast={i === list.length - 1} parMap={parMap} parCountsDone={parCountsDone} onParCount={handleParCount} isDupWarning={cartDupWarning === p.id} isMobile={isMobile} isAdmin={isAdmin} />;
                      })}
                    </>
                  )}
                </>
              )}
          </div>
        </>
      )}

      {activeTab === 'external' && (
        <>
          <p style={{ fontSize: '0.85rem', color: C.mid, marginBottom: '1.25rem' }}>External supplier orders from stocktake. Review quantities then mark as ordered.</p>
          {!Object.keys(bySup).length ? (
            <div style={{ background: C.cream, borderRadius: '10px', border: `1px solid ${C.border}`, padding: '2.5rem', textAlign: 'center', color: C.mid }}>No pending external orders.<br /><span style={{ fontSize: '0.82rem' }}>Complete an external stocktake to generate order references.</span></div>
          ) : Object.entries(bySup).map(([sup, scarts]) => (
            <div key={sup} style={{ marginBottom: '1.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.65rem' }}>
                <span style={{ background: C.greenLight, color: C.green, padding: '0.2rem 0.65rem', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 700 }}>{sup}</span>
                <span style={{ fontSize: '0.75rem', color: C.mid }}>{scarts.length} order{scarts.length !== 1 ? 's' : ''}</span>
              </div>
              {scarts.map(req => <ExternalOrderCard key={req.id} req={req} onUpdate={updated => { if (!updated) setRequests(prev => prev.filter(r => r.id !== req.id)); else setRequests(prev => prev.map(r => r.id === updated.id ? updated : r)); }} />)}
            </div>
          ))}
          {extConf.length > 0 && (
            <>
              <p style={{ ...SL, marginTop: '0.5rem' }}>Ordered</p>
              {extConf.map(r => (
                <div key={r.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.green}`, borderRadius: '8px', padding: '0.7rem 1rem', display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: C.charcoal }}>{r.venue} Order</div>
                    <div style={{ fontSize: '0.72rem', color: C.mid }}>{r.orderFrom || r.order_from}{(r.placedBy || r.placed_by) && <> · <strong>{r.placedBy || r.placed_by}</strong></>}{r.delivery_date && <span style={{ color: C.skyText, fontWeight: 600, marginLeft: '0.4rem' }}>· delivery {fmtDate(r.delivery_date)}</span>}</div>
                  </div>
                  <span style={{ fontSize: '0.74rem', color: C.mid }}>{(r.items || []).length} items</span>
                  <StatusPill status={r.status} />
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}